import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import './styles.css';
import {
  Alert,
  FormControl,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { NormalButton } from 'components/atoms/Button';
import Content from './Content';
import Backstage from 'interfaces/Backstage.interface';
import Loader from 'components/atoms/loader';
import { lockContentStateSetting } from 'APIs/utils';
import { IMessage } from 'interfaces/message.interface';
import { handleLockMessages } from 'utils/Firebase.util';
import { useAppSelector } from 'app/hooks';
import { selectAuth } from 'features/auth/authSlice';
import { UserData } from 'interfaces/UserData.interface';
import { useDispatch } from 'react-redux';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { lg: '480px', xs: '350px' },
  bgcolor: '#292739',
  boxShadow: 24,
  color: 'white',
  borderRadius: '25px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  height: '90vh',
  outline: 'none',
};

interface ILockContentModal {
  children: JSX.Element;
  backstagePosts?: Backstage[];
  messages?: IMessage[];
  handlePremium?: (price: string) => void;
  user?: UserData;
}

const LockContentModal = ({
  children,
  backstagePosts,
  handlePremium,
  messages,
  user,
}: ILockContentModal) => {
  const [open, setOpen] = React.useState(false);
  const [selectedPosts, setSelectedPosts] = React.useState<number[]>([]);
  const [selectedMessages, setSelectedMessages] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [, setSubscribed] = React.useState<boolean>(false);
  const [, setTransaction] = React.useState<boolean>(false);
  const dispatch = useDispatch();
  const [error, setError] = React.useState<string>('');
  const [selectedCurrentPost, setSelectedCurrentPost] =
    React.useState<boolean>(false);
  const [price, setPrice] = React.useState<string>('');
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const authState = useAppSelector(selectAuth);
  const CardImg = require('assets/AuctionBg.png');

  const handleLockContent = async () => {
    if (price === '') setError('Please enter a valid price.');
    else if (!(selectedMessages.length > 0)) {
      if (handlePremium && selectedCurrentPost) {
        if (selectedPosts.length > 0) {
          await lockContentStateSetting(
            setLoading,
            selectedPosts,
            price,
            setSelectedPosts,
            setSuccess,
            setError
          );
          handlePremium(price);
        } else {
          handlePremium(price);
          handleClose();
        }
      } else {
        await lockContentStateSetting(
          setLoading,
          selectedPosts,
          price,
          setSelectedPosts,
          setSuccess,
          setError
        );
      }
    } else {
      await handleLockMessages(
        authState,
        selectedMessages,
        setLoading,
        setSuccess,
        setError,
        setTransaction,
        setSubscribed,
        dispatch,
        true,
        false,
        user
      );
    }
  };

  const addOrRemoveSelectedPost = (
    id: number | null,
    setSelected: React.SetStateAction<any>
  ) => {
    if (id !== null)
      if (selectedPosts.includes(id)) {
        setSelectedPosts(posts => posts.filter(post => post !== id));
        setSelected(false);
      } else {
        setSelectedPosts(posts => [...posts, id]);
        setSelected(true);
      }
  };

  const addOrRemoveSelectedMessage = (
    id: string | null,
    setSelected: React.SetStateAction<any>
  ) => {
    if (id !== null)
      if (selectedMessages.includes(id)) {
        setSelectedMessages(posts => posts.filter(post => post !== id));
        setSelected(false);
      } else {
        setSelectedMessages(posts => [...posts, id]);
        setSelected(true);
      }
  };

  return (
    <div>
      <span onClick={handleOpen}>{children}</span>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <IconButton
            sx={{ position: 'absolute', right: 20, top: 20, color: 'white' }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              margin: '30px 30px 0px 30px',
              padding: '60px',
              textAlign: 'center',
              fontWeight: '600',
            }}
            id="modal-modal-title"
            variant="h5"
            component="h2"
          >
            Set a price for content you'd like to lock
          </Typography>
          <Typography variant="h6" component="h3">
            <FormControl>
              <TextField
                placeholder="0.00"
                required
                sx={{
                  input: {
                    color: 'white',
                    fontSize: '18px',
                    maxWidth: '100px',
                    paddingX: '10px',
                  },
                }}
                onChange={e => setPrice(e.target.value)}
                id="input-with-icon-textfield"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <span style={{ color: 'white', fontSize: '18px' }}>
                        USD
                      </span>
                    </InputAdornment>
                  ),
                }}
                variant="standard"
                color="info"
              />
            </FormControl>
          </Typography>
          <Typography
            id="modal-modal-description"
            sx={{ my: 6, opacity: '0.6' }}
          >
            Select content that should be locked for this post.
          </Typography>
          <Stack direction={'column'} sx={{ marginY: '10px' }}>
            {success && (
              <Alert onClose={() => setSuccess(false)} severity="success">
                Content has been locked successfully.
              </Alert>
            )}
            {error.length > 0 && (
              <Alert onClose={() => setError('')} severity="error">
                {error}
              </Alert>
            )}
          </Stack>
          <Box className="content-container">
            {handlePremium && (
              <Content
                price={price}
                handlePremium={handlePremium}
                addOrRemovePost={addOrRemoveSelectedPost}
                addOrRemoveMessage={addOrRemoveSelectedMessage}
                current={true}
                setSelectedCurrentPost={setSelectedCurrentPost}
              />
            )}
            {/* {backstagePosts
              ?.filter(
                post =>
                  (post.images && post.images.length > 0) ||
                  (post.videos && post.videos.length > 0)
              )
              .map(post => (
                <Content
                  addOrRemovePost={addOrRemoveSelectedPost}
                  setSelectedCurrentPost={setSelectedCurrentPost}
                  data={post}
                  price={price}
                  addOrRemoveMessage={addOrRemoveSelectedMessage}
                  image={post.images[0] || post.videos[0]}
                />
              ))} */}
            {messages?.map(message => (
              <Content
                key={message.id}
                setSelectedCurrentPost={setSelectedCurrentPost}
                message={message}
                price={price}
                addOrRemovePost={addOrRemoveSelectedPost}
                addOrRemoveMessage={addOrRemoveSelectedMessage}
                image={message.text || (message.type === 'nft' && CardImg)}
              />
            ))}
          </Box>
          <NormalButton
            text={loading ? <Loader size="2" /> : 'SET PRICE'}
            color="yellow"
            sx={{
              width: '70%',
              poistion: 'absolute',
              bottom: '50px',
              zIndex: 15,
            }}
            onClick={handleLockContent}
          />
          <span className="modal-span"></span>
        </Box>
      </Modal>
    </div>
  );
};

export default LockContentModal;
